import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  createMaintenanceEvent,
  deleteMaintenanceEvent,
  getMaintenanceEvent,
  listMaintenanceEvents,
  runMaintenanceEventNow,
  updateMaintenanceEvent,
} from './api';
import type { MaintenanceEvent, MaintenanceEventInput } from './types';

function notifyTestDispatch(result: MaintenanceEvent, action: 'create' | 'update' | 'run') {
  const dispatch = result.testDispatch;
  if (!dispatch?.attempted) return;

  if (dispatch.delivered) {
    const verb = action === 'run' ? 'triggered' : action === 'update' ? 're-triggered' : 'sent';
    toast.success('Test reminders processed', {
      description: `Scheduler ${verb} ${dispatch.sentBuckets} reminder email${dispatch.sentBuckets === 1 ? '' : 's'}.`,
    });
    return;
  }

  const title = action === 'run'
    ? 'Test run completed but nothing was sent'
    : action === 'update'
      ? 'Test event updated but nothing was sent'
      : 'Test event created but nothing was sent';
  const details = dispatch.lastError
    || dispatch.triggerError
    || dispatch.warning
    || 'Check scheduler and alerts_api connectivity.';

  toast.warning(title, {
    description: dispatch.errorCount
      ? `${details} (${dispatch.errorCount} delivery error${dispatch.errorCount === 1 ? '' : 's'})`
      : details,
  });
}

export const maintenanceEventsKeys = {
  all: ['maintenance-events'] as const,
  list: (accountId?: string) => [...maintenanceEventsKeys.all, 'list', accountId ?? null] as const,
  detail: (id: string) => [...maintenanceEventsKeys.all, 'detail', id] as const,
};

export function useMaintenanceEventsList(accountId?: string) {
  return useQuery({
    queryKey: maintenanceEventsKeys.list(accountId),
    queryFn: () => listMaintenanceEvents(accountId),
  });
}

export function useMaintenanceEvent(id: string | undefined) {
  return useQuery({
    queryKey: id ? maintenanceEventsKeys.detail(id) : ['maintenance-events', 'detail', null],
    queryFn: () => getMaintenanceEvent(id as string),
    enabled: !!id,
  });
}

export function useCreateMaintenanceEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MaintenanceEventInput) => createMaintenanceEvent(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: maintenanceEventsKeys.all });
      toast.success('Maintenance event created');
      notifyTestDispatch(data, 'create');
    },
    onError: (error: Error) => {
      toast.error('Failed to create maintenance event', { description: error.message });
    },
  });
}

export function useUpdateMaintenanceEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<MaintenanceEventInput> }) =>
      updateMaintenanceEvent(id, patch),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: maintenanceEventsKeys.all });
      queryClient.invalidateQueries({ queryKey: maintenanceEventsKeys.detail(variables.id) });
      toast.success('Maintenance event updated');
      notifyTestDispatch(data, 'update');
    },
    onError: (error: Error) => {
      toast.error('Failed to update maintenance event', { description: error.message });
    },
  });
}

export function useDeleteMaintenanceEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMaintenanceEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maintenanceEventsKeys.all });
      toast.success('Maintenance event deleted');
    },
    onError: (error: Error) => {
      toast.error('Failed to delete maintenance event', { description: error.message });
    },
  });
}

export function useRunMaintenanceEventNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => runMaintenanceEventNow(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: maintenanceEventsKeys.all });
      queryClient.invalidateQueries({ queryKey: maintenanceEventsKeys.detail(data.id) });
      notifyTestDispatch(data, 'run');
    },
    onError: (error: Error) => {
      toast.error('Failed to run test event', { description: error.message });
    },
  });
}
